/**
 * Cornerstone3D, initialised once and lazily.
 *
 * **NOTHING HERE IS IMPORTED AT MODULE SCOPE BY A SCREEN.** The library
 * and its workers are the largest thing this frontend has ever taken on
 * -- measured at 438 KB gzipped plus a 135 KB decode worker -- and the
 * candidate's sitting must not pay for it on first paint. Every entry
 * point below is async and imports on demand, so the cost lands when a
 * viewer actually mounts.
 *
 * `init()` is idempotent and concurrency-safe: two viewports mounting in
 * the same frame share one promise rather than racing two initialisations
 * of the same global registry.
 */

import { ratioText } from "./room";

export interface Cornerstone {
  core: typeof import("@cornerstonejs/core");
  tools: typeof import("@cornerstonejs/tools");
  loader: typeof import("@cornerstonejs/dicom-image-loader");
}

let pending: Promise<Cornerstone> | null = null;

/**
 * Asks the reader for a label's text and answers `done` with it, or with
 * null to give up.
 *
 * Cornerstone's own default is `window.prompt`, which this app does not
 * use anywhere (`styles/dialogs.test.ts`): it cannot be styled, blocks
 * the main thread and arrives prefixed with the hostname. The screen
 * that owns the text label registers its own sheet here while mounted.
 */
export type TextPrompt = (initial: string, done: (text: string | null) => void) => void;

let textPrompt: TextPrompt | null = null;

export function setTextPrompt(prompt: TextPrompt | null): void {
  textPrompt = prompt;
}

/**
 * The tool group every diagnostic viewport joins.
 *
 * ONE group for the whole app rather than one per viewport: the bindings
 * are identical everywhere by design (see the scope's "conform, do not
 * invent"), and a group per viewport is how two viewers end up with
 * different mouse behaviour after somebody edits one of them.
 */
export const TOOL_GROUP_ID = "sk-diagnostic";

/**
 * A SECOND tool group, for the reformatted views.
 *
 * Not the same one, and not a matter of tidiness: MPR's defining tool is
 * Crosshairs, which owns the left button and links three viewports to
 * each other. Adding it to the stack group would change what a left-drag
 * does on every ordinary viewer in the app, and removing it again on
 * every unmount is the kind of bookkeeping that works until one path
 * throws. Two groups, each with its own bindings, and a viewport belongs
 * to exactly one.
 */
export const MPR_TOOL_GROUP_ID = "sk-diagnostic-mpr";

async function load(): Promise<Cornerstone> {
  const [core, tools, loader] = await Promise.all([
    import("@cornerstonejs/core"),
    import("@cornerstonejs/tools"),
    import("@cornerstonejs/dicom-image-loader"),
  ]);

  // ONE WebGL CONTEXT PER RENDERING ENGINE, not Cornerstone's default of
  // seven. Browsers keep about sixteen live and silently kill the OLDEST
  // when a page asks for more ("Too many active WebGL contexts"). At seven
  // apiece the reading room's stack engine and one volume view were
  // fourteen, and changing the MPR layout builds a new engine before the
  // old one's contexts are collected -- so two layout changes were enough
  // to kill the stack cells' own contexts mid-read. Cornerstone itself
  // drops to one on mobile; the room has at most four viewports an engine.
  // Cornerstone deep-merges this over its defaults; the type asks for all of them.
  await core.init({ rendering: { webGlContextCount: 1 } } as Parameters<typeof core.init>[0]);
  await loader.init({
    // The files this system serves are RLE Lossless, which decodes
    // without a wasm codec -- openjph, openjpeg, libjpeg-turbo and
    // charls are never fetched. That is a direct consequence of
    // CONSTRUCTING the stored file rather than keeping what arrived,
    // and it is worth 600 KB of codecs.
    maxWebWorkers: Math.min(navigator.hardwareConcurrency || 2, 4),
  });
  await tools.init();

  registerTools(tools);
  return { core, tools, loader };
}

/**
 * THE BINDINGS, and they are the whole argument of section 4 of the
 * scope in one function.
 *
 * A radiologist's hands already know a viewer. Right-drag windows,
 * middle-drag pans, the wheel stacks and does not zoom, and none of it
 * needs a tool to be selected first. Every one of these is what a hand
 * expects before the eye has decided anything -- and in a system that
 * ASSESSES people, a broken convention costs the candidate rather than
 * merely annoying them.
 */
function registerTools(tools: typeof import("@cornerstonejs/tools")) {
  const {
    WindowLevelTool,
    PanTool,
    ZoomTool,
    StackScrollTool,
    LengthTool,
    ProbeTool,
    AngleTool,
    EllipticalROITool,
    RectangleROITool,
    CobbAngleTool,
    EraserTool,
    ArrowAnnotateTool,
    ToolGroupManager,
    Enums,
  } = tools;

  // Cobb angle, Erase, the text label and Ratio arrived with the reading
  // room (2026-10-07). They join the shared group PASSIVE, so the
  // existing viewer, which never selects them, behaves exactly as it did.
  const all = [
    WindowLevelTool,
    PanTool,
    ZoomTool,
    StackScrollTool,
    LengthTool,
    ProbeTool,
    AngleTool,
    EllipticalROITool,
    RectangleROITool,
    CobbAngleTool,
    EraserTool,
    ArrowAnnotateTool,
  ];
  for (const tool of all) {
    tools.addTool(tool);
  }

  // ANNOTATIONS MUST BE LEGIBLE OVER THE IMAGE, which the defaults are
  // not. Cornerstone draws its readout as bare green text: over a lung
  // window -- white parenchyma, black airways -- "Mean: -354 HU" is
  // unreadable across half its own length, and a measurement nobody can
  // read is a measurement nobody trusts. A dark backing fixes it
  // without changing the colour a radiologist expects an annotation to
  // be.
  //
  // **`setDefaultToolStyles` REPLACES THE WHOLE SET, it does not merge**
  // -- `this.config.default = styles`, one line, no spread. Passing the
  // three keys above on their own dropped `color`, `lineWidth` and
  // `visibility` with them, and every annotation then drew invisibly:
  // the tool armed, the drag registered, nothing appeared, and nothing
  // was logged. Same shape as the panel sheet's stage-by-stage merge,
  // and the same failure -- a write that looks like an update and is a
  // replacement.
  const styles = tools.annotation.config.style;
  const current = styles.getDefaultToolStyles();
  styles.setDefaultToolStyles({
    ...current,
    global: {
      ...current.global,
      shadow: true,
      textBoxBackground: "rgba(0, 0, 0, 0.72)",
      textBoxFontSize: "13px",
    },
  });

  if (ToolGroupManager.getToolGroup(TOOL_GROUP_ID)) return;
  const group = ToolGroupManager.createToolGroup(TOOL_GROUP_ID);
  if (!group) return;

  for (const tool of all) {
    if (tool !== ArrowAnnotateTool) group.addTool(tool.toolName);
  }

  // Without a registered prompt there is nobody to ask, so a new label
  // is given up (Cornerstone removes it) and an edit keeps its text.
  group.addTool(ArrowAnnotateTool.toolName, {
    getTextCallback: (done: (text: string | null) => void) =>
      textPrompt ? textPrompt("", done) : done(null),
    changeTextCallback: (
      annotation: { data: { text?: string } },
      _detail: unknown,
      done: (text: string) => void,
    ) => {
      const was = annotation.data.text ?? "";
      if (!textPrompt) return done(was);
      textPrompt(was, (text) => done(text?.trim() ? text : was));
    },
  });

  // RATIO IS THE LENGTH TOOL UNDER ITS OWN NAME, so its lines are told
  // apart from ordinary lengths and drawn paired: the first on an image
  // reads A, the next B with its percentage of A. Pairing is by creation
  // order, which `getAllAnnotations` keeps.
  group.addToolInstance("Ratio", LengthTool.toolName, {
    getTextLines: (data: unknown, targetId: string) => {
      const all = tools.annotation.state.getAllAnnotations();
      const own = all.find((a) => a.data === data);
      const image = own?.metadata?.referencedImageId;
      const siblings = all.filter(
        (a) => a.metadata?.toolName === "Ratio" && a.metadata?.referencedImageId === image,
      );
      const stats = (a: (typeof all)[number]) =>
        (a.data.cachedStats as Record<string, { length?: number | null; unit?: string }> | undefined)?.[
          targetId
        ];
      const lines = ratioText(
        siblings.map((a) => stats(a)?.length ?? null),
        siblings.findIndex((a) => a.data === data),
        own ? (stats(own)?.unit ?? "mm") : "mm",
      );
      return lines.length ? lines : undefined;
    },
  });

  const { MouseBindings } = Enums;

  registerMprGroup(tools);
  registerRoomVolumeGroups(tools);

  // Right-drag: window and level. The single most-used gesture in
  // radiology, and it is never a mode.
  group.setToolActive(WindowLevelTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Secondary }],
  });
  // Middle-drag: pan.
  group.setToolActive(PanTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Auxiliary }],
  });
  // Wheel: stack. NOT zoom -- this is the one convention the viewer
  // being replaced already got right.
  group.setToolActive(StackScrollTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Wheel }],
  });
  // Left-drag is the only binding a measurement can take, so it is the
  // one the screen switches between. It starts on Pan rather than on a
  // measurement: a left-drag that unexpectedly draws a ruler across
  // somebody's liver is worse than one that does nothing.
  group.setToolActive(PanTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Primary }],
  });

  return group;
}

/**
 * The reformatted views' own group.
 *
 * Window/level and stack-scroll keep the bindings a hand already knows;
 * the left button goes to Crosshairs, which is how every PACS drives an
 * MPR. Scrolling one pane moves the crosshair in the other two, which is
 * the whole reason to have three.
 */
function registerMprGroup(tools: typeof import("@cornerstonejs/tools")) {
  const {
    WindowLevelTool,
    PanTool,
    ZoomTool,
    StackScrollTool,
    CrosshairsTool,
    ToolGroupManager,
    Enums,
  } = tools;

  tools.addTool(CrosshairsTool);
  if (ToolGroupManager.getToolGroup(MPR_TOOL_GROUP_ID)) return;
  const group = ToolGroupManager.createToolGroup(MPR_TOOL_GROUP_ID);
  if (!group) return;

  for (const tool of [
    WindowLevelTool,
    PanTool,
    ZoomTool,
    StackScrollTool,
    CrosshairsTool,
  ]) {
    group.addTool(tool.toolName);
  }

  const { MouseBindings } = Enums;
  group.setToolActive(WindowLevelTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Secondary }],
  });
  group.setToolActive(PanTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Auxiliary }],
  });
  group.setToolActive(StackScrollTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Wheel }],
  });
  group.setToolActive(CrosshairsTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Primary }],
  });
}

/** The reading room's volume views: the orthogonal planes, linked by the
 *  crosshair. */
export const ROOM_VOLUME_GROUP_ID = "sk-room-volume";
/** A volume viewport the crosshair must NOT reach: a lone plane, or the
 *  fourth cell's second window on the axial, which is parallel to the
 *  axial and has no line to draw on it. */
export const ROOM_PLAIN_GROUP_ID = "sk-room-plain";

/**
 * Each plane's colour, by viewport id, set by the room as it builds its
 * viewports. The crosshair draws the line FOR a plane in that plane's
 * colour, which is what lets a reader tell, on the coronal, which line is
 * the axial they are scrolling.
 */
export const planeColours = new Map<string, string>();

/**
 * The room's own groups rather than MprStage's, because the room CONFIGURES
 * the crosshair (colours, no rotation, no slab handles) and the existing
 * diagnostic viewer's reformat must not change underneath it.
 */
function registerRoomVolumeGroups(tools: typeof import("@cornerstonejs/tools")) {
  const { WindowLevelTool, PanTool, ZoomTool, StackScrollTool, CrosshairsTool, ToolGroupManager, Enums } =
    tools;
  const { MouseBindings } = Enums;
  const navigation = (group: NonNullable<ReturnType<typeof ToolGroupManager.createToolGroup>>) => {
    for (const tool of [WindowLevelTool, PanTool, ZoomTool, StackScrollTool]) group.addTool(tool.toolName);
    group.setToolActive(WindowLevelTool.toolName, { bindings: [{ mouseButton: MouseBindings.Secondary }] });
    group.setToolActive(PanTool.toolName, { bindings: [{ mouseButton: MouseBindings.Auxiliary }] });
    group.setToolActive(StackScrollTool.toolName, { bindings: [{ mouseButton: MouseBindings.Wheel }] });
  };

  if (!ToolGroupManager.getToolGroup(ROOM_VOLUME_GROUP_ID)) {
    const group = ToolGroupManager.createToolGroup(ROOM_VOLUME_GROUP_ID);
    if (group) {
      navigation(group);
      // Rotating a reference line makes an OBLIQUE reformat, and slab
      // handles on the line duplicate the slab select -- both off. A
      // reader moves the crosshair; they do not tilt the planes.
      group.addTool(CrosshairsTool.toolName, {
        getReferenceLineColor: (viewportId: string) => planeColours.get(viewportId),
        getReferenceLineControllable: () => true,
        getReferenceLineDraggableRotatable: () => false,
        getReferenceLineSlabThicknessControlsOn: () => false,
      });
    }
  }
  if (!ToolGroupManager.getToolGroup(ROOM_PLAIN_GROUP_ID)) {
    const group = ToolGroupManager.createToolGroup(ROOM_PLAIN_GROUP_ID);
    if (group) {
      navigation(group);
      group.setToolActive(PanTool.toolName, { bindings: [{ mouseButton: MouseBindings.Primary }] });
    }
  }
}

export function init(): Promise<Cornerstone> {
  pending ??= load().catch((error) => {
    // Cleared so a transient failure -- a worker blocked by an
    // extension, a wasm fetch that timed out -- can be retried by the
    // next mount rather than poisoning the page until reload.
    pending = null;
    throw error;
  });
  return pending;
}

/** Whether the library has already loaded, for a caller deciding whether
 *  to show a spinner at all. */
export function isReady(): boolean {
  return pending !== null;
}
